import {
    Box,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    styled,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { BlackInfo, BlueDateIcon, BlueDropDownIcon, DropDownIcons } from '../../assets/SvgIcons';
import React, { useEffect, useState } from 'react';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createStyles, makeStyles } from '@material-ui/core';
import { MuiStyledOptions, SxProps } from '@mui/system';
import dayjs, { Dayjs } from '../../utils/dayjs';
import { FormikErrors } from 'formik';
import { currencyConversion, formatCurrency } from '../../utils/utilityFunctions';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';

interface CustomeButtonProps {
    lookType: string;
    btnText: string;
    onClick?: () => void;
    disabled?: boolean;
    type?: string;
    boxStyle?: any;
    textStyle?: SxProps<Theme>;
    icon?: JSX.Element;
    textVariant?: string;
    isLoading?: boolean;
}

interface PickUpInputProps {
    isLoading: boolean;
    inputType: 'number' | 'text';
    name?: string;
    defaultValue?: string | number;
    // setGlobalData?: React.Dispatch<React.SetStateAction<GetGloabalDataType>>
    handleOnChange?: (name: string, value: number) => void;
    error?: string;
    minValue?: number;
    setFieldValue?: (name: string, value: number) => void;
    value?: string | number;
    label?: string;
}

const preventNegativeValues = (e: React.KeyboardEvent) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()

export const PickUpInput = (props: PickUpInputProps) => {
    const {
        isLoading,
        inputType,
        name = '',
        defaultValue,
        handleOnChange,
        error,
        minValue,
        setFieldValue,
        value,
        label
    } = props;
    const { palette, typography } = useTheme();
    const pickUpDigit = [
        { digit: 5, label: 'L', addedZero: '00,000' },
        { digit: 7, label: 'C', addedZero: '00,00,000' },
    ];
    const [amount, setAmount] = useState(defaultValue ? defaultValue.toString() : '');
    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);


    useEffect(() => {
        if (isLoading) setAmount(defaultValue ? defaultValue.toString() : '');
    }, [isLoading, defaultValue])


    const handleDigitalChange = (item: { digit: number; label: string; addedZero: string }) => {
        setAmount((pre: string) => {
            // console.log(item.digit, pre.length)
            const result: any =
                (pre.length - 1) >= item.digit ? pre : pre + '0'.repeat(item.digit - (pre.length - 1));
            const result2 = result.toString();

            handleOnChange && handleOnChange(name, parseInt(result2));
            return result;
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(Number(e.target.value).toString());
        handleOnChange && handleOnChange(name, parseInt(Number(e.target.value).toString()));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500" fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || 'SIP Amount'}
                    </Typography>
                )}
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography
                        variant="body_400"
                        color={error ? palette.error[500] : palette.text.secondary}>
                        Min: {`${formatCurrency({ value: Number(minValue ?? 0), locale, currencyCode })}`}
                    </Typography>
                )}
            </Box>
            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        background: error ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        borderRadius: '10px',
                        gap: '10px',
                        height: '50px',
                    }}>
                    <Typography variant="body_500" mt={'-2px'} color={error ? palette.error[500] : palette.primary[500]}>
                        {String.fromCharCode(parseInt(currencySymbol, 16))}
                    </Typography>
                    <Box
                        // {...field}
                        placeholder='Enter'
                        id={name}
                        name={name}
                        value={amount}
                        // defaultValue={defaultValue}
                        type={inputType || 'number'}
                        component={'input'}
                        onPaste={e => e.preventDefault()}
                        onKeyDown={preventNegativeValues}
                        onChange={handleInputChange}
                        onWheel={event => { event.currentTarget.blur(); }}
                        width={'100%'}
                        border={'none'}
                        sx={{
                            '::placeholder': {
                                color: error ? `${palette.error[500]}80` : palette.primary[500],
                                // marginTop: '8px',
                            },
                            overflow: 'hidden',
                            fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                            // WebkitAppearance: 'none'
                        }}
                        style={{
                            background: 'transparent',
                            outline: 'none',
                            fontFamily: typography.fontFamily,
                            ...typography.body_500,
                        }}
                        color={error ? palette.error[500] : palette.primary[500]}
                    />
                </Box>
            )}

            {error && (
                isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_500" color={palette.error[500]}>
                    {error || ''}
                </Typography>

            )}

            {isLoading ? (
                <Skeleton variant="text" animation="wave" width={200} />
            ) : (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
                    <BlackInfo />
                    <Typography variant="body_400" color={palette.text.secondary}>
                        {currencyConversion(locale, amount || 0) || 'N/A'}
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                {pickUpDigit?.map((item, i) => {
                    const isEnabled = amount.length <= item.digit && Number(amount) > 0;
                    return (
                        <Box
                            key={i}
                            component={'button'}
                            type="button"
                            aria-label={`Append zeros to make amount ${item.label === 'L' ? 'in Lakhs' : 'in Crores'}`}
                            disabled={!isEnabled}
                            onClick={() => isEnabled && handleDigitalChange(item)}
                            style={{
                                padding: '0px 10px',
                                cursor: isEnabled ? 'pointer' : 'not-allowed',
                                opacity: isEnabled ? 1 : .5,
                            }}
                            sx={{
                                background: palette.primary[50],
                                border: `1px solid ${palette.primary[500]}`,
                                borderRadius: '20px',
                                width: 'fit-content',
                                '&:focus-visible': {
                                    outline: `2px solid ${palette.primary[500]}`,
                                    outlineOffset: '2px',
                                },
                            }}>
                            {isLoading ? (
                                <Skeleton variant="text" animation="wave" width={100} sx={{ borderRadius: '8px' }} />
                            ) : (
                                <Typography color={palette.primary[500]} variant="body_500" fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}>
                                    {item.addedZero} | {item.label}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

interface CustomeNumberInputProps {
    isLoading: boolean;
    textType?: 'number' | 'text';
    label?: string;
    minValue?: number;
    boxStyle?: SxProps<Theme>;
    minlabel?: string;
    warningText?: string;
    name?: string;
    defaultValue?: number | string;
    handleOnChange?: (name: string, value: number) => void;
    error?: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined;
    removeIcons?: boolean;
    infoAlign?: 'left' | 'center' | 'right';
    hardCodeValue?: number | string;
    showError?: boolean;
    disable?: boolean
}
export const CustomeNumberInput = (props: CustomeNumberInputProps) => {
    const {
        isLoading,
        label,
        textType,
        minValue,
        boxStyle,
        minlabel,
        warningText,
        handleOnChange,
        name = '',
        defaultValue = '',
        error,
        removeIcons,
        infoAlign = 'left',
        hardCodeValue = 0,
        showError = false,
        disable = false
    } = props;
    const { palette, typography } = useTheme();
    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const [amount, setAmount] = useState(defaultValue || '');

    useEffect(() => {
        if (isLoading) {
            setAmount(defaultValue || '');
        }
    }, [isLoading, defaultValue]);

    useEffect(() => {
        if (hardCodeValue) setAmount(hardCodeValue)
        else setAmount('')
    }, [hardCodeValue])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(e.target.value);
        handleOnChange && handleOnChange(name, Number(e.target.value));
    };

    // console.log('amount', amount)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'nowrap', opacity: disable ? .5 : 1 }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500"
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || ''}
                    </Typography>
                )}
                {minlabel &&
                    (isLoading ? (
                        <Skeleton variant="text" animation="wave" width={100} />
                    ) : (
                        <Typography variant="body_400"
                            fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}
                            lineHeight={1.3}
                            sx={{
                                textWrap: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: disable ? .5 : 1,

                            }}
                            color={error ? palette.error[500] : palette.text.secondary}>
                            {minlabel}
                        </Typography>
                    ))}
            </Box>
            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        background: error ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        borderRadius: '10px',
                        gap: '10px',
                        height: '50px',
                        opacity: disable ? .5 : 1,
                        ...boxStyle,
                    }}>
                    {!removeIcons && (
                        <Typography
                            variant="body_500"
                            color={error ? palette.error[500] : palette.primary[500]}>
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    )}
                    <Box
                        component={'input'}
                        placeholder='Enter'
                        //hardCode because of Redeem checkbox
                        value={amount}
                        onPaste={e => e.preventDefault()}
                        defaultValue={defaultValue}
                        disabled={disable}
                        onChange={handleInputChange}
                        onKeyDown={preventNegativeValues}
                        onWheel={event => { event.currentTarget.blur(); }}
                        type={textType || 'text'}
                        width={'100%'}
                        sx={{
                            '::placeholder': {
                                color: error ? `${palette.error[500]}80` : palette.primary[200],
                                // marginTop: '8px',
                            },
                            fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                            opacity: disable ? .9 : 1,
                            cursor: disable ? 'not-allowed' : 'text',

                        }}
                        border={'none'}
                        style={{
                            background: 'transparent',
                            outline: 'none',
                            fontFamily: typography.fontFamily,
                            ...typography.body_500,
                        }}
                        color={error ? palette.error[500] : palette.primary[500]}
                    />
                </Box>
            )}
            {minValue?.toString() ? (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
                    <Typography
                        variant="body_400"
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: disable ? .5 : 1
                        }}
                        color={error ? palette.error[500] : palette.text.secondary}>
                        Min. number of installments is {minValue}
                    </Typography>
                </Box>
            ) : null}
            {warningText === 'amount' &&
                (isLoading ? (
                    <Skeleton variant="text" animation="wave" width={200} />
                ) : (
                    amount && (
                        <Box
                            component={'label'}
                            sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center', justifyContent: infoAlign, opacity: disable ? .5 : 1 }}>
                            <BlackInfo />
                            <Typography variant="body_400" color={palette.text.secondary}>
                                {currencyConversion(locale, typeof amount === 'string' ? parseInt(amount) : amount) || 'Zero'}
                            </Typography>
                        </Box>
                    )
                ))}
            {showError && (
                isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_500" color={palette.error[500]}>
                    {error || ''}
                </Typography>

            )}
        </Box>
    );
};

interface CustomeBlueDropDownProps {
    isLoading?: boolean;
    boxStyle?: any;
    label?: string;
    list?: Array<{ value: string; label: string }>;
    defaultValue?: string;
    handleOnChange?: (name: string, value: string) => void;
    name?: string;
    error?: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined;
    disabled?: boolean;
}
export const CustomeBlueDropDown = (props: CustomeBlueDropDownProps) => {
    const {
        isLoading,
        boxStyle,
        label,
        list = [],
        defaultValue,
        handleOnChange,
        name = '',
        error = '',
        disabled = false,
    } = props;
    const { palette, typography } = useTheme();
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        if (isLoading) {
            setValue(defaultValue);
        }
    }, [isLoading, defaultValue]);

    const handleChange = (event: SelectChangeEvent) => {
        setValue(event.target.value);
        if (handleOnChange) {
            handleOnChange(name, event.target.value);
        }
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500" color={palette.text.secondary}
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: disabled ? '0.5' : '1',
                        }}
                        fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}>
                        {label || 'Select Account'}
                    </Typography>
                )}
            </Box>

            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Select
                    // name={name || ''}
                    disableUnderline
                    variant="standard"
                    style={{
                        height: '50px',
                        borderRadius: '10px',
                        background: error ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        color: error ? palette.error[500] : palette.primary[500],
                        opacity: disabled ? '0.5' : '1',
                        // borderRight: '2px solid ' + palette.text.secondary + 'b2',
                        ...(boxStyle || {}),
                    }}
                    sx={{
                        '.MuiSelect-standard': {
                            '&:focus': {
                                background: 'transparent',
                            },
                        },
                        fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                    }}
                    defaultValue={defaultValue || `a`}
                    disabled={disabled}
                    // value={value}
                    onChange={handleChange}
                    IconComponent={(props) => (
                        <BlueDropDownIcon {...props} cursor={'pointer'} style={{ marginRight: '15px' }} />
                    )}>
                    <MenuItem key="a" value="a" disabled>
                        Select
                    </MenuItem>
                    {list?.map((item, index) => {
                        return (
                            <MenuItem key={index} value={item.value} >
                                {item.label}
                            </MenuItem>
                        );
                    })}
                    {list?.length === 0 && (
                        <MenuItem value={10} disabled>
                            No Data
                        </MenuItem>
                    )}
                </Select>
            )}
            {error &&
                (isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_500" color={palette.error[500]}>
                    {error || ''}
                </Typography>)
            }
        </Box>
    );
};

interface CustomeBlueDatePickerProps {
    label?: string;
    name?: string;
    handleOnChange?: (name: string, value: string) => void;
    defaultValue?: string;
    error?: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined;
    disablePast?: boolean;
    isLoading?: boolean;
    disableDateList?: Array<number>;
    minDate?: Dayjs
}

export const CustomeBlueDatePicker = (props: CustomeBlueDatePickerProps) => {
    const { palette, typography } = useTheme();
    const { label, name = '', handleOnChange, defaultValue, error, disablePast, isLoading, disableDateList = [], minDate } = props;
    const [value, setValue] = useState(dayjs(defaultValue, 'DD/MM/YYYY') || '');

    const onChange = (value: Date | any) => {
        setValue(value);
        if (handleOnChange) {
            handleOnChange(name, value);
        }
    };

    useEffect(() => {
        if (isLoading) {
            setValue(dayjs(defaultValue, 'DD/MM/YYYY') || '');
        }
    }, [isLoading, defaultValue]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isLoading && (
                <Skeleton variant="text" animation="wave" width={100} />
            )}
            {!isLoading && (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Typography variant="body_500" fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || 'SIP Date'}
                    </Typography>
                </Box>
            )}
            {isLoading && (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            )}
            {!isLoading &&
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer
                        components={['DatePicker']}
                        sx={{
                            paddingTop: 0,
                            borderRadius: '10px',
                            background: error ? palette.error[50] : palette.primary[50],
                            '&:focus': {
                                border: 'none',
                            },
                            '&:hover': {
                                border: 'none',
                            },
                        }}>
                        <DatePicker
                            label=""
                            slots={{
                                openPickerIcon: BlueDateIcon,
                            }}
                            value={value}
                            disablePast={disablePast}
                            format="DD/MM/YYYY"
                            onChange={onChange}
                            // defaultValue={dayjs(defaultValue)}
                            shouldDisableDate={(date: any) => {
                                return disableDateList?.length > 0 && !disableDateList?.includes(dayjs(date).date());
                            }}
                            sx={{
                                fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                                width: '100%',
                                '& input': {
                                    ...typography.body_500,
                                    fontFamily: typography.fontFamily,
                                    height: '50px',
                                    padding: '0 15px',
                                    color: error ? palette.error[500] : palette.primary[500],
                                },
                                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { border: 'none' }, // at page load
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 'none' }, // at hover state
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                }, // at focused state
                            }}
                            minDate={minDate}
                        />
                    </DemoContainer>
                </LocalizationProvider>
            }
            {error &&
                (
                    isLoading ? <Skeleton variant="text" animation="wave" width={200} /> :
                        <Typography variant="body_500" color={palette.error[500]}>
                            {error || ''}
                        </Typography>
                )}
        </Box>
    );
};

export const CustomeButton = (props: CustomeButtonProps) => {
    const { palette } = useTheme();
    const { lookType, btnText, onClick, disabled, type, icon, boxStyle, isLoading } = props;
    return (
        <Box
            component={'button'}
            disabled={disabled}
            type="submit"
            onClick={() => (disabled || isLoading) ? null : onClick && onClick()}
            sx={{
                cursor: (disabled || isLoading) ? 'not-allowed' : 'pointer',
                padding: '13.5px',
                width: '100%',
                borderRadius: '10px',
                border: lookType === 'contained' ? 'none' : `1px solid ${palette.primary[500]}`,
                background: lookType === 'contained' ? palette.primary[500] : 'transparent',
                opacity: (disabled || isLoading) ? 0.5 : 1,
                ...boxStyle,
            }}>
            {icon && icon}
            {isLoading ? <CircularProgress color={'warning'} size={20} /> :
                <Typography
                    variant="body_700"
                    fontSize={{ lg: '18px', md: '18px', sm: '14px', xs: '14px' }}
                    color={lookType === 'contained' ? palette.textDark.primary : palette.primary[500]}>
                    {btnText}
                </Typography>
            }
        </Box>
    );
};

export const CustomeModalButton = (props: CustomeButtonProps) => {
    const { palette } = useTheme();
    const { lookType, btnText, onClick, boxStyle, textStyle = {}, isLoading, disabled } = props;
    return (
        <Box
            component={'button'}
            onClick={() => (disabled || isLoading) ? null : onClick && onClick()}
            sx={{
                cursor: (disabled || isLoading) ? 'not-allowed' : 'pointer',
                outline: 'none',
                padding: { lg: '13px', md: '13px', sm: '10px 0', xs: '10px 0' },
                width: '100%',
                borderRadius: { lg: '10px', md: '10px', sm: '4px', xs: '4px' },
                border: lookType === 'contained' ? 'none' : `1px solid #CDCDCD`,
                background: lookType === 'contained' ? palette.primary[500] : 'transparent',
                opacity: (disabled || isLoading) ? 0.5 : 1,
                ...boxStyle,
            }}>
            {isLoading ? <CircularProgress color={'warning'} size={20} /> :
                <Typography
                    variant="subtitle_300"
                    fontWeight={600}
                    noWrap
                    padding={{ lg: '0 0', md: '0 0', sm: '0 18.25px', xs: '0 18.25px' }}
                    fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}
                    textOverflow={'ellipsis'}
                    overflow={'hidden'}
                    whiteSpace={'nowrap'}
                    color={lookType === 'contained' ? palette.textDark.primary : palette.text.primary}
                    sx={{
                        ...textStyle
                    }}
                >
                    {btnText}
                </Typography>
            }
        </Box>
    );
};

type CustomeModalDropdownType = {
    label: string;
    onChange: (name: string, value: string) => void;
    name: string;
    data: { label: string; value: string }[];
    value?: string;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            minWidth: 120,
        },
        selectEmpty: {
            borderRadius: '10px',

            // marginTop: theme.spacing(2),
        },
    })
);


const SelectStyled = styled(FormControl)(({ theme: { palette } }) => ({
    // height: '50px',
    '& label.Mui-focused': {
        color: `${palette.text.secondary}`,
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: `${palette.text.secondary}80`,
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: `${palette.text.secondary}80`,
            fontSize: '16px',
        },
        '&:hover fieldset': {
            borderColor: `${palette.text.secondary}80`,
        },
        '&.Mui-focused fieldset': {
            borderColor: `${palette.text.secondary}80`,
        },
    },
}));

export const CustomeModalDropDown = (props: CustomeModalDropdownType) => {
    const { palette, typography } = useTheme();
    const { label, onChange, data, name, value } = props;



    // const accountNumber = value ? value : data?.[0]?.value;

    // useEffect(() => {
    //     console.log("DATA", data)
    //     setAccountNumber(parseInt(data?.[0]?.value))
    // }, [data])

    // console.log("ACCOUNNTTT", accountNumber)
    // console.log("BANK ACC", data);
    const classes = useStyles();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <SelectStyled variant="outlined" size="small" className={classes.formControl}>
                <InputLabel id="demo-simple-select-outlined-label">{label}</InputLabel>
                <Select
                    className={classes.selectEmpty}
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    value={value}
                    onChange={(e: any) => {
                        console.log("E", e);
                        // setAccountNumber(e.target.value)
                        // console.log('e', e.target.value)
                        onChange(name, e.target.value)
                    }}
                    label={label}
                    IconComponent={(props) => <DropDownIcons {...props} style={{ marginRight: '15px' }} />}>
                    {data?.map((bank) => {
                        return (
                            <MenuItem key={bank?.value} value={bank?.value}>{bank?.label}</MenuItem>
                        )
                    })}
                </Select>
            </SelectStyled>
        </Box>
    );
};
